import {
  OrdersStatus,
  OrdersPaymentStatus,
  OrdersDeliveryType,
  OrdersSortDirection,
  BuildingTypes,
  OrderPaymentChannel,
  PartnerConfirmationStatus,
} from '@/models/orders.model';
import { Roles } from '@/models/admins.model';

const ORDER_COMPOSITION_EDIT_ROLES = new Set<Roles>([
  Roles.SUPER_ADMIN,
  Roles.CLIENT_SERVICE_HEAD,
  Roles.CLIENT_MANAGER,
]);

export enum OrderCommentAuthorRole {
  PARTNER = 'partner',
  ADMIN = 'admin',
}

export enum OrdersFiltersFields {
  ID = 'id',
  NUMBER = 'number',
  CONTACT_NAME = 'contact_name',
  CONTACT_PHONE = 'contact_phone',
  DATE_FROM = 'date_from',
  DATE_TO = 'date_to',
  PAYMENT_DATE_FROM = 'payment_date_from',
  PAYMENT_DATE_TO = 'payment_date_to',
  LIMIT = 'limit',
  PAGE = 'page',
  STATUS = 'status',
  PAYMENT_STATUS = 'payment_status',
  PAYMENT_CHANNEL = 'payment_channel',
  DELIVERY_TYPE = 'delivery_type',
  SORT_DIRECTION = 'sort_direction',
  STORE_IDS = 'store_ids',
  UTM_SOURCE = 'utm_source',
  MANAGER_ID = 'manager_id',
  PARTNER_CONFIRMATION_STATUS = 'partner_confirmation_status',
}

export type TGetOrdersQuery = {
  [OrdersFiltersFields.LIMIT]?: number;
  [OrdersFiltersFields.PAGE]?: number;
  [OrdersFiltersFields.ID]?: string;
  [OrdersFiltersFields.NUMBER]?: number;
  [OrdersFiltersFields.CONTACT_NAME]?: string;
  [OrdersFiltersFields.CONTACT_PHONE]?: string;
  [OrdersFiltersFields.DATE_FROM]?: string;
  [OrdersFiltersFields.DATE_TO]?: string;
  [OrdersFiltersFields.PAYMENT_DATE_FROM]?: string;
  [OrdersFiltersFields.PAYMENT_DATE_TO]?: string;
  [OrdersFiltersFields.STATUS]?: OrdersStatus;
  [OrdersFiltersFields.PAYMENT_STATUS]?: OrdersPaymentStatus;
  [OrdersFiltersFields.PAYMENT_CHANNEL]?: OrderPaymentChannel;
  [OrdersFiltersFields.DELIVERY_TYPE]?: OrdersDeliveryType;
  [OrdersFiltersFields.SORT_DIRECTION]?: OrdersSortDirection;
  [OrdersFiltersFields.STORE_IDS]?: string[];
  [OrdersFiltersFields.MANAGER_ID]?: string;
  [OrdersFiltersFields.UTM_SOURCE]?: string;
  [OrdersFiltersFields.PARTNER_CONFIRMATION_STATUS]?: PartnerConfirmationStatus;
};

export enum OrderDetailsFormFieldsNames {
  PRODUCTS = 'products',
  COMMENTS = 'comments',
}

export type TOrderDetailsFormValues = {
  [OrderDetailsFormFieldsNames.PRODUCTS]: {
    [key: string]: number;
  };
  [OrderDetailsFormFieldsNames.COMMENTS]: {
    [key: string]: string;
  };
};

export enum UpdateOrderDeliveryFormFieldsNames {
  DELIVERY_TYPE = 'delivery_type',
  SETTLEMENT = 'settlement_id',
  COMMENT = 'comment',
  ADDRESS = 'address',
  BUILDING_TYPE = 'building_type',
  FLOOR = 'floor',
  FLAT_NUMBER = 'flat_number',
  ELEVATOR = 'freight_elevator',
  DELIVERY_DEPARTMENT = 'delivery_department',
  DELIVERY_WAREHOUSE = 'delivery_warehouse_id',
}

export type TUpdateOrderDeliveryFormValues = {
  [UpdateOrderDeliveryFormFieldsNames.DELIVERY_TYPE]?: OrdersDeliveryType;
  [UpdateOrderDeliveryFormFieldsNames.SETTLEMENT]?: string;
  [UpdateOrderDeliveryFormFieldsNames.COMMENT]?: string;
  [UpdateOrderDeliveryFormFieldsNames.ADDRESS]?: string;
  [UpdateOrderDeliveryFormFieldsNames.BUILDING_TYPE]?: BuildingTypes;
  [UpdateOrderDeliveryFormFieldsNames.FLOOR]?: number;
  [UpdateOrderDeliveryFormFieldsNames.FLAT_NUMBER]?: string;
  [UpdateOrderDeliveryFormFieldsNames.ELEVATOR]?: '0' | '1';
  [UpdateOrderDeliveryFormFieldsNames.DELIVERY_DEPARTMENT]?: string;
  [UpdateOrderDeliveryFormFieldsNames.DELIVERY_WAREHOUSE]?: string;
};

export enum UpdateOrderClientFormFieldsNames {
  NAME = 'contact_name',
  PHONE = 'contact_phone',
  EMAIL = 'contact_email',
  DESCRIPTION = 'description',
}

export type TUpdateOrderClientFormValues = {
  [UpdateOrderClientFormFieldsNames.NAME]?: string;
  [UpdateOrderClientFormFieldsNames.PHONE]?: string;
  [UpdateOrderClientFormFieldsNames.EMAIL]?: string;
  [UpdateOrderClientFormFieldsNames.DESCRIPTION]?: string;
};

export const isOrderContentEditable = (
  status?: OrdersStatus,
  deliveryType?: OrdersDeliveryType,
  role?: Roles | string | null,
): boolean => {
  if (!status || status === OrdersStatus.CANCELLED) {
    return false;
  }

  if (role === Roles.LOGISTICIAN) {
    return false;
  }

  if (deliveryType === OrdersDeliveryType.PICKUP) {
    return true;
  }

  if (deliveryType === OrdersDeliveryType.NOVA_POSHTA) {
    // Composition lock only after transferred (ready_for_ttn stays editable for managers).
    if (status === OrdersStatus.TRANSFERRED) {
      return role === Roles.SUPER_ADMIN;
    }

    return !!role && ORDER_COMPOSITION_EDIT_ROLES.has(role as Roles);
  }

  return role === Roles.SUPER_ADMIN;
};

export const requiresCompositionChangeReason = (
  status?: OrdersStatus,
  paymentStatus?: OrdersPaymentStatus,
): boolean => {
  const fulfillmentStatuses = new Set([
    OrdersStatus.ACCEPTED,
    OrdersStatus.READY_FOR_TTN,
    OrdersStatus.TRANSFERRED,
    OrdersStatus.AWAITING,
    OrdersStatus.COMPLETED,
  ]);

  if (status && fulfillmentStatuses.has(status)) {
    return true;
  }

  const paidLikeStatuses = new Set([
    OrdersPaymentStatus.PAID,
    OrdersPaymentStatus.PREPAID,
    OrdersPaymentStatus.AWAITING_ADDITIONAL_PAYMENT,
    OrdersPaymentStatus.REQUIRES_PARTIAL_REFUND,
  ]);

  return !!paymentStatus && paidLikeStatuses.has(paymentStatus);
};

export const commentEditStatuses = new Set([
  OrdersStatus.NEW,
  OrdersStatus.PROCESSING,
  OrdersStatus.TRANSFERRED,
  OrdersStatus.ACCEPTED,
  OrdersStatus.READY_FOR_TTN,
  OrdersStatus.AWAITING,
  OrdersStatus.COMPLETED,
  OrdersStatus.CANCELLED,
]);

export const paymentEditStatuses = new Set([
  OrdersStatus.PROCESSING,
  OrdersStatus.TRANSFERRED,
  OrdersStatus.ACCEPTED,
  OrdersStatus.READY_FOR_TTN,
  OrdersStatus.AWAITING,
  OrdersStatus.COMPLETED,
  OrdersStatus.CANCELLED,
]);

export enum MeasureUnitCode {
  PIECE = 'piece',
  GRAM = 'gram',
  KILOGRAM = 'kilogram',
  TON = 'ton',
  LITER = 'liter',
  METER = 'meter',
  LINEAR_METER = 'linear_meter',
  SQUARE_METER = 'square_meter',
  CUBIC_METER = 'cubic_meter',
  PACKAGE = 'package',
  SET = 'set',
  PAIR = 'pair',
  SERVICE = 'service',
  BAY = 'bay',
  PACK = 'pack',
}
